import { FC, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { Box, CircularProgress, Tooltip } from '@mui/material';

import { Role } from '@/apis/admin-api/types';
import { useGetMySDKUser } from '@/hooks';
import { PrimaryButton } from '@/components/Shared/Buttons';
import { TrblAddIcon } from '@/components/Icons';

import {
  useGetOrganizationById,
  useGetOrganizationUsersAccesses,
  useGetSubscription,
  useGetTokenStatus,
  useUserActions,
  useCreateUser,
  useAddUserToOrganization,
} from './hooks';

import { OrganizationDetails } from './components/OrganizationDetails';
import { SubscriptionDetailsSDK } from './components/SubscriptionDetailsSDK';
import { UsersGrid } from './components/UsersGrid';
import { AddUserSidepanel } from './components/AddUserSidepanel';
import { EditUserSidepanel } from './components/EditUserSidepanel';

import { UserAccess } from './types';
import { useGetOrganizationMcpApiKeys } from '../McpServersPage/hooks';

export const AdministrationPage: FC = () => {
  const { data: sdkUser, isLoading: isLoadingSdkUser, isFetched } = useGetMySDKUser();
  const organizationId = sdkUser?.organizationId ?? '';
  const currentUserId = sdkUser?.id ?? '';
  const isAdmin = sdkUser?.roles?.includes(Role.Admin) ?? false;

  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [editingUser, setEditingUser] = useState<UserAccess | null>(null);

  const { data: organization, isLoading: isLoadingOrganization } = useGetOrganizationById(organizationId);
  const { data: subscriptionDetails } = useGetSubscription(organizationId);
  const { data: productAccesses } = useGetOrganizationUsersAccesses(organizationId);
  const { data: tokenStatus } = useGetTokenStatus(organizationId);
  const { data: mcpApiKeys } = useGetOrganizationMcpApiKeys(isAdmin);
  const mcpKeyCounts = useMemo(
    () =>
      mcpApiKeys
        ?.filter((key) => !key.revokedAt && new Date(key.expiresAt) > new Date())
        .reduce<Record<string, number>>((counts, key) => {
          counts[key.userId] = (counts[key.userId] ?? 0) + 1;
          return counts;
        }, {}),
    [mcpApiKeys]
  );

  const {
    updateAdminSettings,
    updateUserSDKAccess,
    updateUserMfaRequirement,
    updateUser,
    deleteUser,
    resendSetPasswordEmail,
    inProgress,
  } = useUserActions(organizationId);

  const { mutate: createUser, isPending: isCreatingUserPending } = useCreateUser();
  const { mutate: addUserToOrganization } = useAddUserToOrganization(organizationId);

  const isLoading = isLoadingOrganization || inProgress || isCreatingUserPending;

  const sdkSeatsAvailable = subscriptionDetails?.sdkProduct
    ? subscriptionDetails.sdkProduct.activeSeats < subscriptionDetails.sdkProduct.maxSeats
    : false;

  const handleAddUser = (email: string) => {
    createUser(
      { email, organizationId },
      {
        onSuccess: (createdUser) => {
          addUserToOrganization({
            userId: createdUser.id,
            organizationId,
            products: ['TSDK'],
          });
        },
      }
    );
    setIsCreatingUser(false);
  };

  if (isLoadingSdkUser || !isFetched) {
    return (
      <Box component="div" display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress sx={{ color: '#5BF6D1' }} />
      </Box>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/no-access" replace />;
  }

  if (!organizationId) {
    return (
      <Box component="div" display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress sx={{ color: '#5BF6D1' }} />
      </Box>
    );
  }

  return (
    <Box component="div" display="flex" flexDirection="column" gap="6px" height="100%" width="100%" overflow="auto">
      <Box component="div" display="flex" flexDirection="row" gap="20px">
        <Box component="div" width={396} mb="10px">
          <OrganizationDetails organization={organization} />
        </Box>
        {subscriptionDetails?.sdkProduct && (
          <Box component="div" width={396} mb="10px">
            <SubscriptionDetailsSDK
              subscriptionDetails={subscriptionDetails.sdkProduct}
              chargebeeSubscription={subscriptionDetails.sdkChargebeeSubscription}
              tokensLeft={tokenStatus?.tokensLeftInPackages}
            />
          </Box>
        )}
      </Box>

      <Box component="div" display="flex" alignItems="end" justifyContent="space-between">
        <p style={{ fontWeight: '600' }}>Users</p>
        <Tooltip title={sdkSeatsAvailable ? '' : 'You can not add new users — please add more seats'}>
          <span>
            <PrimaryButton
              label="Add user"
              disabled={!sdkSeatsAvailable}
              icon={<TrblAddIcon fill="none" />}
              onClick={() => setIsCreatingUser(true)}
              width={150}
              sx={{ marginBottom: '2px' }}
            />
          </span>
        </Tooltip>
      </Box>

      <Box component="div" flex="1 1 auto" minHeight={400}>
        <UsersGrid
          organizationId={organizationId}
          currentUserId={currentUserId}
          users={productAccesses ?? []}
          subscription={subscriptionDetails ?? ({} as never)}
          isLoading={isLoading}
          mcpKeyCounts={mcpKeyCounts}
          onToggleAdmin={updateAdminSettings}
          onToggleSDKAccess={updateUserSDKAccess}
          onToggleMfa={updateUserMfaRequirement}
          isUsingSso={organization?.organization.useSso ?? false}
          onEditUser={setEditingUser}
          onDeleteUser={deleteUser}
          onResendSetPasswordEmail={resendSetPasswordEmail}
        />
      </Box>

      {editingUser && (
        <EditUserSidepanel
          user={editingUser}
          isSubmitting={inProgress}
          onCancel={() => setEditingUser(null)}
          onSubmit={(firstName, lastName) =>
            updateUser({ userId: editingUser.id, firstName, lastName }, { onSuccess: () => setEditingUser(null) })
          }
        />
      )}

      {isCreatingUser && <AddUserSidepanel onCancel={() => setIsCreatingUser(false)} onSubmit={handleAddUser} />}
    </Box>
  );
};
